/**
 * FeaturedLessonCard.jsx — Feb 2026 mid-month.
 *
 * "This Week's Featured Lesson" card rendered on FreeHome. Shows the
 * admin-pinned lesson (or nothing if not pinned) and lets a free-tier
 * teacher email themselves a 7-day delivery link. The download itself
 * happens on the delivery page (`/featured-lesson/:token`), not here.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Mail, ArrowRight, X } from "lucide-react";
import {
  fetchFeaturedLesson,
  requestFeaturedLessonEmail,
} from "../lib/featuredLessonApi";
import { useAuth } from "../contexts/AuthContext";

const FeaturedLessonCard = () => {
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchFeaturedLesson()
      .then((d) => {
        if (!cancelled) setCard(d);
      })
      .catch(() => {
        if (!cancelled) setCard({ pinned: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (modalOpen && !email && user?.email) {
      setEmail(user.email);
    }
  }, [modalOpen, user, email]);

  // Card exists solely to drive the CASL email capture. Hide it whole
  // when the flow is gated off (no live email button ⇒ no dead-end card).
  if (loading || !card || !card.pinned || !card.email_capture_enabled) return null;

  const meta = [card.grade, card.subject].filter(Boolean).join(" · ");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      await requestFeaturedLessonEmail(email);
      setSent(true);
      toast.success("Check your inbox — the lesson is on its way.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send. Try again in a moment.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    // Reset next tick so the transition isn't jarring
    setTimeout(() => setSent(false), 250);
  };

  return (
    <>
      <div
        data-testid="featured-lesson-card"
        className="mt-8 sm:mt-10 rounded-2xl border border-[#96ded1]/70 bg-gradient-to-br from-white to-[#96ded1]/15 p-6 sm:p-8 shadow-sm"
      >
        <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/80">
              <Sparkles className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
              This Week's Featured Lesson
            </p>
            <h2
              data-testid="featured-lesson-card-title"
              className="mt-2 font-black text-xl sm:text-2xl tracking-tight text-[#1c2d3d]"
            >
              {card.activity_name}
            </h2>
            {meta ? (
              <p
                data-testid="featured-lesson-card-meta"
                className="mt-1 text-xs font-extrabold uppercase tracking-[0.14em] text-[#4a6984]"
              >
                {meta}
              </p>
            ) : null}
            {card.tagline ? (
              <p className="mt-3 text-sm sm:text-base italic text-[#1c2d3d] leading-relaxed">
                {card.tagline}
              </p>
            ) : null}
            {card.overview ? (
              <p className="mt-2 text-sm text-[#4a6984] leading-relaxed line-clamp-3">
                {card.overview}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="featured-lesson-card-cta"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030] w-full md:w-auto justify-center"
          >
            Email it to me
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {modalOpen ? (
        <div
          data-testid="featured-lesson-email-modal"
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl border border-[#4a6984]/15 shadow-xl max-w-md w-full p-6 sm:p-7 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeModal}
              data-testid="featured-lesson-modal-close"
              className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-md text-[#4a6984] hover:bg-[#f4f5f7]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            {sent ? (
              <div data-testid="featured-lesson-modal-sent">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
                  <Mail className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                  Sent
                </p>
                <h3 className="mt-2 font-black text-xl text-[#1c2d3d]">
                  Check your inbox
                </h3>
                <p className="mt-3 text-sm text-[#4a6984] leading-relaxed">
                  We just emailed the download link to <span className="font-extrabold text-[#1c2d3d]">{email}</span>. The link works for 7 days — no rush.
                </p>
                <button
                  type="button"
                  onClick={closeModal}
                  data-testid="featured-lesson-modal-done"
                  className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030] w-full justify-center"
                >
                  Got it
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit}>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
                  <Sparkles className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                  Featured lesson
                </p>
                <h3 className="mt-2 font-black text-xl text-[#1c2d3d]">
                  {card.activity_name}
                </h3>
                <p className="mt-3 text-sm text-[#4a6984] leading-relaxed">
                  We'll email you a private link (good for 7 days) with the printable lesson package and student slide deck.
                </p>
                <label className="mt-5 block">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
                    Your email
                  </span>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="featured-lesson-modal-email-input"
                    placeholder="you@example.com"
                    className="mt-1.5 w-full rounded-md border border-[#4a6984]/25 bg-white px-3 py-2.5 text-sm text-[#1c2d3d] placeholder:text-[#4a6984]/50 focus:outline-none focus:ring-2 focus:ring-[#96ded1] focus:border-[#96ded1]"
                  />
                </label>
                <p className="mt-2 text-[11px] leading-relaxed text-[#4a6984]/80" data-testid="featured-lesson-consent-note">
                  We'll only use this to email you this one lesson — we won't add you to any list.{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#1c2d3d]">
                    Privacy Policy
                  </a>
                </p>
                <button
                  type="submit"
                  disabled={sending || !email}
                  data-testid="featured-lesson-modal-submit"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030] disabled:opacity-60 disabled:cursor-not-allowed w-full justify-center"
                >
                  {sending ? "Sending…" : "Send me the link"}
                </button>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default FeaturedLessonCard;
